import { Model } from '@contember/schema';
import { applicationType as zApplicationType } from '../../common';
const profiles = {
    all: {
        reasonWeights: {
            pluginEntity: 1,
            connectionEntity: 1,
            coreFunctionality: 1,
            userInteraction: 1,
            complexRelationships: 1,
            highDataVolatility: 1,
            complexDataStructure: 1,
            userRoleManagement: 1,
            reportingRequirements: 1,
            externalIntegration: 1,
            largeDataVolume: 1,
            notManagedBySelectFields: 1,
            multipleUniqueConstraints: 1,
            complexUniqueConstraints: 1,
            noEnumFields: 1,
            fewSelectManageableFields: 1,
            hasRelationships: 1,
            hasLongTextFields: 1,
            uniqueConstraintOnPrimaryKey: 1,
            deferrableUniqueConstraints: 1,
            highFieldCount: 1,
            multipleRelationships: 1,
            nestedStructures: 1,
            hasOneToManyOrManyToMany: 1,
            multipleRelationshipTypes: 1,
            hasBidirectionalRelationships: 1,
            hasSelfReferences: 1,
            translatableFields: 1,
            hasPluginFields: 1,
        },
        crudThreshold: 0,
        fieldComplexityThreshold: 0,
        relationshipComplexityThreshold: 0,
        uniqueConstraintThreshold: 0,
    },
    cms: {
        reasonWeights: {
            pluginEntity: 0,
            connectionEntity: 0,
            coreFunctionality: 5,
            userInteraction: 4,
            complexRelationships: 3,
            highDataVolatility: 2,
            complexDataStructure: 4,
            userRoleManagement: 3,
            reportingRequirements: 2,
            externalIntegration: 3,
            largeDataVolume: 4,
            notManagedBySelectFields: 2,
            multipleUniqueConstraints: 1,
            complexUniqueConstraints: 1,
            noEnumFields: 2,
            fewSelectManageableFields: 2,
            hasRelationships: 1,
            hasLongTextFields: 3,
            uniqueConstraintOnPrimaryKey: 1,
            deferrableUniqueConstraints: 1,
            highFieldCount: 3,
            multipleRelationships: 2,
            nestedStructures: 3,
            hasOneToManyOrManyToMany: 2,
            multipleRelationshipTypes: 2,
            hasBidirectionalRelationships: 2,
            hasSelfReferences: 2,
            translatableFields: 2,
            hasPluginFields: 2,
        },
        crudThreshold: 15,
        fieldComplexityThreshold: 6,
        relationshipComplexityThreshold: 4,
        uniqueConstraintThreshold: 2,
    },
    crm: {
        reasonWeights: {
            pluginEntity: 0,
            connectionEntity: 0,
            coreFunctionality: 5,
            userInteraction: 5,
            complexRelationships: 4,
            highDataVolatility: 4,
            complexDataStructure: 3,
            userRoleManagement: 4,
            reportingRequirements: 4,
            externalIntegration: 4,
            largeDataVolume: 3,
            notManagedBySelectFields: 2,
            multipleUniqueConstraints: 2,
            complexUniqueConstraints: 2,
            noEnumFields: 1,
            fewSelectManageableFields: 1,
            hasRelationships: 2,
            hasLongTextFields: 2,
            uniqueConstraintOnPrimaryKey: 2,
            deferrableUniqueConstraints: 2,
            highFieldCount: 3,
            multipleRelationships: 3,
            nestedStructures: 2,
            hasOneToManyOrManyToMany: 3,
            multipleRelationshipTypes: 3,
            hasBidirectionalRelationships: 3,
            hasSelfReferences: 2,
            translatableFields: 2,
            hasPluginFields: 2,
        },
        crudThreshold: 15,
        fieldComplexityThreshold: 7,
        relationshipComplexityThreshold: 5,
        uniqueConstraintThreshold: 3,
    },
    generic: {
        reasonWeights: {
            pluginEntity: 0,
            connectionEntity: 0,
            coreFunctionality: 5,
            userInteraction: 5,
            complexDataStructure: 5,
            largeDataVolume: 5,
            complexRelationships: 4,
            highDataVolatility: 4,
            userRoleManagement: 4,
            externalIntegration: 4,
            reportingRequirements: 3,
            notManagedBySelectFields: 3,
            highFieldCount: 3,
            hasOneToManyOrManyToMany: 3,
            hasBidirectionalRelationships: 3,
            hasSelfReferences: 3,
            multipleUniqueConstraints: 2,
            complexUniqueConstraints: 2,
            uniqueConstraintOnPrimaryKey: 2,
            deferrableUniqueConstraints: 2,
            multipleRelationships: 2,
            multipleRelationshipTypes: 2,
            nestedStructures: 2,
            translatableFields: 2,
            hasPluginFields: 2,
            noEnumFields: 1,
            fewSelectManageableFields: 1,
            hasRelationships: 1,
            hasLongTextFields: 1,
        },
        crudThreshold: 15,
        fieldComplexityThreshold: 7,
        relationshipComplexityThreshold: 5,
        uniqueConstraintThreshold: 3,
    },
};
export class CrudAnalyzer {
    constructor(schema, schemaExtensions, applicationType) {
        Object.defineProperty(this, "schema", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: schema
        });
        Object.defineProperty(this, "schemaExtensions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: schemaExtensions
        });
        Object.defineProperty(this, "applicationType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: applicationType
        });
        Object.defineProperty(this, "profile", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        const parsedType = zApplicationType.safeParse(applicationType);
        this.profile = profiles[parsedType.success ? parsedType.data : 'generic'];
    }
    isPluginEntity(entityName) {
        return Object.values(this.schemaExtensions.plugins).some(plugin => !!plugin.schema?.model.entities[entityName]);
    }
    determineCrudRequirements() {
        return {
            scopeTypes: this.scopeTypesByApplicationType(this.applicationType),
            entities: Object.fromEntries(Object.entries(this.schema?.model.entities ?? {}).map(([entityName, entity]) => [entityName, this.analyzeEntity(entity)])),
        };
    }
    scopeTypesByApplicationType(applicationType) {
        switch (applicationType) {
            case 'cms':
                return ['dataGrid', 'create', 'edit'];
            default:
                return ['dataGrid', 'detail', 'create', 'edit'];
        }
    }
    isConnectionEntity(entity) {
        const OPTIONAL_FIELDS = 2;
        const fields = Object.values(entity.fields);
        const relationFields = fields.filter(field => 'target' in field);
        // Check if the entity has exactly two relations
        if (relationFields.length !== 2) {
            return false;
        }
        const hasCreatedAtField = fields.some(field => field.name === 'createdAt' && field.type === Model.ColumnType.DateTime);
        const hasNoExtraUniqueConstraints = !entity.unique || entity.unique.length === 0;
        const isValidConnectionPattern = (relationFields[0].type === Model.RelationType.ManyHasOne && relationFields[1].type === Model.RelationType.ManyHasOne) ||
            (relationFields[0].type === Model.RelationType.ManyHasMany &&
                relationFields[1].type === Model.RelationType.OneHasOne &&
                'inversedBy' in relationFields[1]) ||
            (relationFields[1].type === Model.RelationType.ManyHasMany &&
                relationFields[0].type === Model.RelationType.OneHasOne &&
                'inversedBy' in relationFields[0]) ||
            (relationFields[0].type === Model.RelationType.ManyHasMany && relationFields[1].type === Model.RelationType.ManyHasMany);
        const additionalFields = fields.filter(field => !('target' in field) && field.name !== 'createdAt' && field.name !== entity.primary);
        const hasAllowedAdditionalFields = additionalFields.length <= OPTIONAL_FIELDS;
        return isValidConnectionPattern && hasNoExtraUniqueConstraints && hasCreatedAtField && hasAllowedAdditionalFields;
    }
    analyzeEntity(entity) {
        const reasons = [];
        if (this.isPluginEntity(entity.name)) {
            return {
                entityName: entity.name,
                needsCrud: false,
                reasons: ['pluginEntity'],
                crudNecessityDetails: {
                    needsCrud: false,
                    score: 0,
                    threshold: 0,
                    contributingReasons: [],
                },
                isPluginEntity: true,
                isConnectionEntity: false,
            };
        }
        if (this.isConnectionEntity(entity)) {
            return {
                entityName: entity.name,
                needsCrud: false,
                reasons: ['connectionEntity'],
                crudNecessityDetails: {
                    needsCrud: false,
                    score: 0,
                    threshold: 0,
                    contributingReasons: [],
                },
                isPluginEntity: false,
                isConnectionEntity: true,
            };
        }
        this.checkComplexDataStructure(entity, reasons);
        this.checkComplexRelationships(entity, reasons);
        this.analyzeReferences(entity, reasons);
        this.checkUniqueConstraints(entity, reasons);
        this.checkEnumFields(entity, reasons);
        const crudNecessity = this.calculateNeedsCrud(reasons, entity.name);
        return {
            entityName: entity.name,
            needsCrud: crudNecessity.needsCrud,
            reasons: reasons,
            crudNecessityDetails: crudNecessity,
            isPluginEntity: false,
            isConnectionEntity: false,
        };
    }
    checkComplexDataStructure(entity, reasons) {
        const fields = Object.values(entity.fields);
        const fieldCount = fields.length;
        let complexityScore = 0;
        complexityScore += fieldCount;
        complexityScore += fields.filter(field => CrudAnalyzer.COMPLEX_FIELD_TYPES.includes(field.type)).length;
        const relationshipFields = fields.filter(field => 'target' in field);
        complexityScore += relationshipFields.length * 2;
        const hasNestedStructures = fields.some(field => field.type === Model.ColumnType.Json);
        if (hasNestedStructures) {
            complexityScore += 3;
        }
        const enumFields = fields.filter(field => field.type === Model.ColumnType.Enum);
        if (enumFields.length > 0) {
            complexityScore += enumFields.length;
        }
        if (complexityScore > CrudAnalyzer.FIELD_COMPLEXITY_THRESHOLD) {
            reasons.push('complexDataStructure');
            if (fieldCount > CrudAnalyzer.FIELD_COMPLEXITY_THRESHOLD) {
                reasons.push('highFieldCount');
            }
            if (relationshipFields.length > 2) {
                reasons.push('multipleRelationships');
            }
            if (hasNestedStructures) {
                reasons.push('nestedStructures');
            }
        }
        return complexityScore;
    }
    checkComplexRelationships(entity, reasons) {
        const fields = Object.values(entity.fields);
        const relationships = fields.filter(field => 'target' in field);
        let complexityScore = 0;
        const relationshipTypes = {
            oneToOne: 0,
            oneToMany: 0,
            manyToOne: 0,
            manyToMany: 0,
        };
        relationships.forEach(relation => {
            switch (relation.type) {
                case Model.RelationType.OneHasOne:
                    relationshipTypes.oneToOne++;
                    complexityScore += 1;
                    break;
                case Model.RelationType.OneHasMany:
                    relationshipTypes.oneToMany++;
                    complexityScore += 2;
                    break;
                case Model.RelationType.ManyHasOne:
                    relationshipTypes.manyToOne++;
                    complexityScore += 1;
                    break;
                case Model.RelationType.ManyHasMany:
                    relationshipTypes.manyToMany++;
                    complexityScore += 3;
                    break;
            }
            if ('joiningColumn' in relation && relation.joiningColumn.onDelete === Model.OnDelete.cascade) {
                complexityScore += 1;
            }
        });
        const hasBidirectionalRelationships = relationships.some(relation => 'inversedBy' in relation && relation.inversedBy !== undefined);
        if (hasBidirectionalRelationships) {
            complexityScore += 2;
        }
        const hasSelfReferences = relationships.some(relation => relation.target === entity.name);
        if (hasSelfReferences) {
            complexityScore += 2;
        }
        if (complexityScore > CrudAnalyzer.RELATIONSHIP_COMPLEXITY_THRESHOLD) {
            reasons.push('complexRelationships');
            // Add more specific reasons
            if (relationshipTypes.oneToMany + relationshipTypes.manyToMany > 0) {
                reasons.push('hasOneToManyOrManyToMany');
            }
            if (Object.values(relationshipTypes).filter(count => count > 0).length > 2) {
                reasons.push('multipleRelationshipTypes');
            }
            if (hasBidirectionalRelationships) {
                reasons.push('hasBidirectionalRelationships');
            }
            if (hasSelfReferences) {
                reasons.push('hasSelfReferences');
            }
        }
        return {
            complexityScore,
            relationshipTypes,
            hasBidirectionalRelationships,
            hasSelfReferences,
        };
    }
    analyzeReferences(entity, reasons) {
        const references = Object.values(this.schema?.model.entities ?? {}).reduce((acc, otherEntity) => {
            const referencingFields = Object.values(otherEntity.fields).filter(field => 'target' in field && field.target === entity.name);
            if (referencingFields.length > 0) {
                acc.push({
                    entity: otherEntity.name,
                    fields: referencingFields.map(field => ({
                        name: field.name,
                        type: field.type,
                    })),
                });
            }
            return acc;
        }, []);
        const referenceCount = references.length;
        const uniqueReferenceTypes = new Set(references.flatMap(ref => ref.fields.map(f => f.type))).size;
        if (referenceCount > 0) {
            if (referenceCount > 3) {
                reasons.push('coreFunctionality');
            }
            if (uniqueReferenceTypes > 1) {
                reasons.push('complexRelationships');
            }
            if (references.some(ref => ref.fields.some(f => f.type === Model.RelationType.OneHasMany || f.type === Model.RelationType.ManyHasMany))) {
                reasons.push('largeDataVolume');
            }
        }
        return { referenceCount, uniqueReferenceTypes, references };
    }
    checkUniqueConstraints(entity, reasons) {
        if (!entity.unique || entity.unique.length === 0) {
            return null;
        }
        const uniqueConstraints = entity.unique;
        const uniqueFieldsSet = new Set(uniqueConstraints.flatMap(constraint => constraint.fields));
        const totalUniqueFields = uniqueFieldsSet.size;
        const multiFieldConstraints = uniqueConstraints.filter(constraint => constraint.fields.length > 1);
        let volatilityScore = 0;
        volatilityScore += uniqueConstraints.length;
        volatilityScore += multiFieldConstraints.length;
        if (uniqueFieldsSet.has(entity.primary)) {
            volatilityScore += 2;
        }
        const deferrableConstraints = uniqueConstraints.filter(constraint => constraint.timing === 'deferrable' || constraint.timing === 'deferred');
        volatilityScore += deferrableConstraints.length * 0.5;
        if (volatilityScore > CrudAnalyzer.UNIQUE_CONSTRAINT_THRESHOLD) {
            reasons.push('highDataVolatility');
            // Add more specific reasons
            if (uniqueConstraints.length > 1) {
                reasons.push('multipleUniqueConstraints');
            }
            if (multiFieldConstraints.length > 0) {
                reasons.push('complexUniqueConstraints');
            }
            if (uniqueFieldsSet.has(entity.primary)) {
                reasons.push('uniqueConstraintOnPrimaryKey');
            }
            if (deferrableConstraints.length > 0) {
                reasons.push('deferrableUniqueConstraints');
            }
        }
        return {
            volatilityScore,
            uniqueConstraintsCount: uniqueConstraints.length,
            totalUniqueFields,
            multiFieldConstraintsCount: multiFieldConstraints.length,
            involvesPrimaryKey: uniqueFieldsSet.has(entity.primary),
            deferrableConstraintsCount: deferrableConstraints.length,
        };
    }
    checkEnumFields(entity, reasons) {
        const fields = Object.values(entity.fields);
        const enumFields = fields.filter(field => field.type === Model.ColumnType.Enum);
        const boolFields = fields.filter(field => field.type === Model.ColumnType.Bool);
        const otherFields = fields.filter(field => !CrudAnalyzer.SELECT_MANAGEABLE_TYPES.includes(field.type));
        const selectManageableFieldsCount = enumFields.length + boolFields.length;
        const totalFieldsCount = fields.length;
        let selectManageabilityScore = 0;
        selectManageabilityScore = selectManageableFieldsCount / totalFieldsCount;
        selectManageabilityScore -= otherFields.length * 0.1;
        const hasRelationships = fields.some(field => 'target' in field);
        if (hasRelationships) {
            selectManageabilityScore -= 0.2;
        }
        const hasLongTextFields = fields.some(field => field.type === Model.ColumnType.String && 'columnType' in field && field.columnType.toLowerCase().includes('text'));
        if (hasLongTextFields) {
            selectManageabilityScore -= 0.2;
        }
        if (selectManageabilityScore < 0.5) {
            reasons.push('notManagedBySelectFields');
            if (enumFields.length === 0) {
                reasons.push('noEnumFields');
            }
            if (selectManageableFieldsCount < CrudAnalyzer.ENUM_FIELD_THRESHOLD) {
                reasons.push('fewSelectManageableFields');
            }
            if (hasRelationships) {
                reasons.push('hasRelationships');
            }
            if (hasLongTextFields) {
                reasons.push('hasLongTextFields');
            }
        }
        return {
            selectManageabilityScore,
            enumFieldsCount: enumFields.length,
            boolFieldsCount: boolFields.length,
            otherFieldsCount: otherFields.length,
            totalFieldsCount,
            hasRelationships,
            hasLongTextFields,
        };
    }
    calculateNeedsCrud(reasons, entityName) {
        const contributingReasons = reasons.map(reason => ({
            reason,
            weight: this.profile.reasonWeights[reason],
        }));
        const score = contributingReasons.reduce((sum, { weight }) => sum + weight, 0);
        const needsCrud = score >= this.profile.crudThreshold;
        return {
            needsCrud,
            score,
            threshold: this.profile.crudThreshold,
            contributingReasons,
        };
    }
}
Object.defineProperty(CrudAnalyzer, "UNIQUE_CONSTRAINT_THRESHOLD", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: 2
});
Object.defineProperty(CrudAnalyzer, "RELATIONSHIP_COMPLEXITY_THRESHOLD", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: 3
});
Object.defineProperty(CrudAnalyzer, "FIELD_COMPLEXITY_THRESHOLD", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: 5
});
Object.defineProperty(CrudAnalyzer, "ENUM_FIELD_THRESHOLD", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: 2
});
Object.defineProperty(CrudAnalyzer, "SELECT_MANAGEABLE_TYPES", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: [Model.ColumnType.Enum, Model.ColumnType.Bool]
});
Object.defineProperty(CrudAnalyzer, "COMPLEX_FIELD_TYPES", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: [Model.ColumnType.Json, Model.ColumnType.DateTime]
});
//# sourceMappingURL=determine-crud-requirements.js.map